<script lang="ts">
    import { browser } from '$app/environment';
    import { onMount, onDestroy } from 'svelte';
    import { goto } from '$app/navigation';
    import { page } from '$app/stores';
    import { fade, slide } from 'svelte/transition';
    import { flip } from 'svelte/animate';
    import { quintOut } from 'svelte/easing';
    import { supabase } from '$lib/supabase';
    import { 
        Grid,
        Maximize2,
        Minimize2,
        X,
        MessageCircle,
        Settings,
        Plus,
        Search,
        Users,
        Filter
    } from 'lucide-svelte';
    import { formatDate } from '$lib/utils/dateUtils';
    import { formatNumber } from '$lib/utils/numberUtils';

    export let data;

    // 型定義
    interface Video {
        id: string;
        title: string;
        thumbnail_url: string;
        channel_id: string;
        published_at: string;
        concurrent_viewers?: number;
        platform: 'youtube' | 'twitch';  // プラットフォーム情報を追加
        [key: string]: any;
    }

    interface Channel {
        id: string;
        name: string;
        thumbnail: string;
        [key: string]: any;
    }

    interface Group {
        id: string;
        name: string;
        color?: string;
        [key: string]: any;
    }

    interface GroupChannel {
        id: string;
        group_id: string;
        [key: string]: any;
    }

    interface DraggedVideo {
        video: Video;
        index: number;
    }

    // 状態管理
    let liveVideos: Video[] = data.liveVideos;
    let selectedVideos: Video[] = data.selectedVideos;
    let channels: Channel[] = data.channels;
    let groups: Group[] = data.groups;
    let groupChannels: GroupChannel[] = data.groupChannels;
    let initialVideoIds: string[] = data.initialVideoIds;
    let isLoading = false;
    let lastUpdated = new Date();
    let updateInterval: ReturnType<typeof setInterval>;
    
    // 配信タイプフィルター
    let streamTypeFilter: 'vtuber' | 'streamer' = 'vtuber';
    
    // レイアウト設定
    let layout = {
        rows: 2,
        columns: 2
    };
    let showSidebar = true;
    let showChat = true;
    let activeVideoIndex = 0;
    let activeChatIndices = [0, 1]; // 表示する2つのチャットのインデックス
    let dualChatMode = false; // 2つのチャットを表示するモード
    let searchQuery = '';
    let selectedGroup = '';
    let filteredVideos: Video[] = liveVideos;
    let isDragging = false;
    let draggedVideo: DraggedVideo | null = null;
    let dropTarget: number | null = null;
    let customLayout = false;
    let mainVideoIndex = 0;
    
    // レスポンシブ対応
    let isMobile = false;
    
    onMount(() => {
        if (browser) {
            isMobile = window.innerWidth < 768;
            window.addEventListener('resize', handleResize);
            
            // 30秒ごとにライブ配信リストを更新
            updateInterval = setInterval(fetchLiveVideos, 30000);
        }
        
        // URLからのビデオIDで初期化
        if (initialVideoIds.length > 0) {
            selectedVideos = data.selectedVideos;
        }
        
        updateFilteredVideos();
    });
    
    onDestroy(() => {
        if (browser) {
            window.removeEventListener('resize', handleResize);
            
            // インターバルをクリア
            if (updateInterval) {
                clearInterval(updateInterval);
            }
        }
    });
    
    function handleResize() {
        isMobile = window.innerWidth < 768;
        
        // モバイルの場合はレイアウトを調整
        if (isMobile) {
            layout = { rows: 1, columns: 1 };
            showSidebar = false;
        } else {
            // デスクトップの場合はデフォルトレイアウトに戻す
            layout = { rows: 2, columns: 2 };
            showSidebar = true;
        }
    }
    
    function updateFilteredVideos() {
        filteredVideos = liveVideos.filter(video => {
            // 検索クエリでフィルタリング
            const matchesSearch = searchQuery === '' || 
                video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                channels.find(c => c.id === video.channel_id)?.name.toLowerCase().includes(searchQuery.toLowerCase());
            
            // グループでフィルタリング
            const matchesGroup = selectedGroup === '' || 
                groupChannels.some(gc => gc.id === video.channel_id && gc.group_id === selectedGroup);
            
            // 配信タイプでフィルタリング
            const channelInfo = channels.find(c => c.id === video.channel_id);
            const groupInfo = channelInfo?.group_id ? groups.find(g => g.id === channelInfo.group_id) : null;
            const isStreamer = groupInfo?.name === 'Streamer';
            const isVtuber = !isStreamer;
            
            const matchesStreamType = 
                (streamTypeFilter === 'streamer' && isStreamer) || 
                (streamTypeFilter === 'vtuber' && isVtuber);
            
            return matchesSearch && matchesGroup && matchesStreamType;
        });
    }
    
    function addVideo(video: Video) {
        // 既に追加されている場合は何もしない
        if (selectedVideos.some(v => v.id === video.id)) return;
        
        selectedVideos = [...selectedVideos, video];
        updateUrl();
    }
    
    function removeVideo(index: number) {
        selectedVideos = selectedVideos.filter((_, i) => i !== index);
        updateUrl();
    }
    
    function updateUrl() {
        const ids = selectedVideos.map(v => v.id).join(',');
        const url = `/multiview?ids=${ids}`;
        goto(url, { replaceState: true });
    }
    
    function setActiveVideo(index: number) {
        activeVideoIndex = index;
        // アクティブビデオのチャットを1つ目に設定
        activeChatIndices[0] = index;
    }
    
    function setActiveChat(index: number, slot: 0 | 1) {
        activeChatIndices[slot] = index;
    }
    
    function changeLayout(rows: number, cols: number) {
        layout = { rows, columns: cols };
        customLayout = false;
    }
    
    function setMainVideo(index: number) {
        if (mainVideoIndex === index && customLayout) {
            // 同じボタンを押したら解除
            customLayout = false;
        } else {
            mainVideoIndex = index;
            customLayout = true;
            // メインビデオのチャットを1つ目に設定
            // activeChatIndices[0] = index;
        }
    }
    
    function toggleSidebar() {
        showSidebar = !showSidebar;
    }
    
    function toggleChat() {
        showChat = !showChat;
    }
    
    function toggleChatMode() {
        dualChatMode = !dualChatMode;
    }
    
    function handleSearch() {
        updateFilteredVideos();
    }
    
    function selectGroup(groupId: string) {
        selectedGroup = groupId === selectedGroup ? '' : groupId;
        updateFilteredVideos();
    }
    
    function selectStreamType(type: 'vtuber' | 'streamer') {
        streamTypeFilter = type;
        updateFilteredVideos();
    }
    
    // ドラッグ&ドロップ機能
    function dragStart(video: Video, index: number) {
        isDragging = true;
        draggedVideo = { video, index };
    }
    
    function dragOver(index: number) {
        dropTarget = index;
    }
    
    function drop() {
        if (draggedVideo !== null && dropTarget !== null && draggedVideo.index !== dropTarget) {
            const newSelectedVideos = [...selectedVideos];
            const [removed] = newSelectedVideos.splice(draggedVideo.index, 1);
            newSelectedVideos.splice(dropTarget, 0, removed);
            selectedVideos = newSelectedVideos;
        }
        
        isDragging = false;
        draggedVideo = null;
        dropTarget = null;
    }
    
    function getChannelName(channelId: string) {
        return channels.find(c => c.id === channelId)?.name || 'Unknown Channel';
    }
    
    function getChannelThumbnail(channelId: string) {
        return channels.find(c => c.id === channelId)?.thumbnail || '';
    }
    
    function getVideoEmbedUrl(video: Video, index: number): string {
        if (video.platform === 'youtube') {
            return `https://www.youtube.com/embed/${video.id}?autoplay=${index === activeVideoIndex ? 1 : 0}&mute=${index !== activeVideoIndex ? 1 : 0}`;
        } else if (video.platform === 'twitch') {
            const channelInfo = channels.find(c => c.id === video.channel_id);
            const twitchId = channelInfo?.twitch_id || '';
            if (!twitchId) return '';
            
            return `https://player.twitch.tv/?channel=${twitchId}&parent=${browser ? window.location.hostname : ''}&autoplay=${index === activeVideoIndex ? 'true' : 'false'}&muted=${index !== activeVideoIndex ? 'true' : 'false'}`;
        }
        return '';
    }
    
    function getChatEmbedUrl(video: Video): string {
        if (video.platform === 'youtube') {
            return `https://www.youtube.com/live_chat?v=${video.id}&embed_domain=${browser ? window.location.hostname : ''}&theme=dark`;
        } else if (video.platform === 'twitch') {
            const channelInfo = channels.find(c => c.id === video.channel_id);
            const twitchId = channelInfo?.twitch_id || '';
            if (!twitchId) return '';
            
            return `https://www.twitch.tv/embed/${twitchId}/chat?parent=${browser ? window.location.hostname : ''}&darkpopout`;
        }
        return '';
    }
    
    $: {
        searchQuery;
        selectedGroup;
        updateFilteredVideos();
    }

    // selectedVideosが変更されたときに視聴履歴に保存する
    $: {
        if (browser && selectedVideos.length > 0) {
            try {
                // 選択された各ビデオをローカルストレージの履歴に追加
                selectedVideos.forEach(video => {
                    const videoId = video.id;
                    
                    let history: string[] = [];
                    const storedHistory = localStorage.getItem('recentlyWatchedVideos');
                    
                    if (storedHistory) {
                        history = JSON.parse(storedHistory);
                    }
                    
                    // 重複を避けるために既存のIDを削除
                    history = history.filter(id => id !== videoId);
                    
                    // 新しいIDを先頭に追加
                    history.unshift(videoId);
                    
                    localStorage.setItem('recentlyWatchedVideos', JSON.stringify(history));
                });
            } catch (error) {
                console.error('Failed to save videos to history:', error);
            }
        }
    }

    async function fetchLiveVideos() {
        if (isLoading) return;
        
        try {
            isLoading = true;
            
            // APIからライブ配信を取得
            const { data: newLiveVideos, error } = await supabase
                .from('video')
                .select('*, live_stats')
                .eq('live_broadcast_content', 'live');
                        
            if (error) {
                console.error('ライブ配信の取得に失敗しました:', error);
                return;
            }
            
            
            // 各ビデオの最新の視聴者数を設定
            if (newLiveVideos && newLiveVideos.length > 0) {
                newLiveVideos.forEach(video => {
                    if (video.live_stats) {
                        // live_statsオブジェクトから最新のタイムスタンプを取得
                        const latestTimestamp = Object.keys(video.live_stats).sort().pop();
                        if (latestTimestamp) {
                            video.concurrent_viewers = video.live_stats[latestTimestamp];
                        } else {
                            console.log(`ビデオID ${video.id} の視聴者数データなし`);
                        }
                    } else {
                        console.log(`ビデオID ${video.id} のlive_statsなし`);
                    }
                });
            }
            
            // 視聴者数でソート
            newLiveVideos.sort((a, b) => (b.concurrent_viewers || 0) - (a.concurrent_viewers || 0));
            
            // 新しいチャンネルIDを抽出
            const existingChannelIds = channels.map(c => c.id);
            const newChannelIds = newLiveVideos
                .map(video => video.channel_id)
                .filter(id => !existingChannelIds.includes(id));
            
            // 新しいチャンネル情報を取得（存在しないチャンネルのみ）
            if (newChannelIds.length > 0) {
                const { data: newChannels, error: channelsError } = await supabase
                    .from('channels')
                    .select('*')
                    .in('id', newChannelIds);
                
                if (channelsError) {
                    console.error('チャンネル情報の取得に失敗しました:', channelsError);
                } else if (newChannels && newChannels.length > 0) {
                    // 既存のチャンネル情報と新しいチャンネル情報をマージ
                    channels = [...channels, ...newChannels];
                    console.log('新しいチャンネル情報を取得しました:', newChannels);
                }
            }
            
            // 新しいデータで更新
            liveVideos = newLiveVideos;
            updateFilteredVideos();
            lastUpdated = new Date();
            
        } catch (err) {
            console.error('ライブ配信の更新中にエラーが発生しました:', err);
        } finally {
            isLoading = false;
        }
    }

    // 時間フォーマット関数
    function formatTime(date: Date): string {
        return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
    }
</script>

<svelte:head>
    <title>マルチビュー | VTGrid</title>
    <meta name="description" content="複数の配信を同時に視聴できるマルチビュー機能" />
</svelte:head>

<div class="multiview-container">
    <!-- ヘッダー -->
    <header class="multiview-header">
        <div class="header-left">
            <h1>マルチビュー</h1>
        </div>
        <div class="header-right">
            <button class="sidebar-toggle-button" on:click={toggleSidebar}>
                {#if showSidebar}
                    <X size={18} />
                    <span>左サイドバーを閉じる</span>
                {:else}
                    <Search size={18} />
                    <span>配信を検索</span>
                {/if}
            </button>
            <div class="layout-controls">
                <div class="layout-buttons">
                    <div class="layout-category">
                        <div class="layout-grid">
                            <button class="layout-option" on:click={() => changeLayout(1, 1)}>
                                <div class="layout-preview layout-1x1"></div>
                                <span>1x1</span>
                            </button>
                            <button class="layout-option" on:click={() => changeLayout(1, 2)}>
                                <div class="layout-preview layout-1x2"></div>
                                <span>1x2</span>
                            </button>
                            <button class="layout-option" on:click={() => changeLayout(2, 2)}>
                                <div class="layout-preview layout-2x2">
                                    <span></span>
                                    <span></span>
                                </div>
                                <span>2x2</span>
                            </button>
                            <button class="layout-option" on:click={() => changeLayout(2, 3)}>
                                <div class="layout-preview layout-2x3">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                                <span>2x3</span>
                            </button>
                            <button class="layout-option" on:click={() => changeLayout(3, 3)}>
                                <div class="layout-preview layout-3x3">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                                <span>3x3</span>
                            </button>
                            <button class="layout-option" on:click={() => changeLayout(1, 3)}>
                                <div class="layout-preview layout-1x3">
                                    <span></span>
                                </div>
                                <span>1x3</span>
                            </button>
                            <button class="layout-option" on:click={() => changeLayout(3, 1)}>
                                <div class="layout-preview layout-3x1">
                                    <span></span>
                                </div>
                                <span>3x1</span>
                            </button>
                            <button class="layout-option" on:click={() => changeLayout(3, 2)}>
                                <div class="layout-preview layout-3x2">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                                <span>3x2</span>
                            </button>
                        </div>
                    </div>
                    {#if selectedVideos.length > 1}
                        <div class="layout-category special-layouts">
                            <div class="layout-grid">
                                {#each selectedVideos as video, i}
                                    <button class="layout-option {customLayout && mainVideoIndex === i ? 'active' : ''}" on:click={() => setMainVideo(i)}>
                                        <div class="layout-preview main-layout-preview">
                                            <div class="main-number">{i+1}</div>
                                        </div>
                                        <span>メイン {i+1}</span>
                                    </button>
                                {/each}
                            </div>
                        </div>
                    {/if}
                </div>
            </div>
            <button class="icon-button" on:click={toggleChat}>
                <MessageCircle size={20} />
            </button>
        </div>
    </header>

    <div class="multiview-content">
        <!-- サイドバー -->
        {#if showSidebar}
            <aside class="multiview-sidebar" transition:slide={{ duration: 300 }}>
                <div class="search-container">
                    <input 
                        type="text" 
                        placeholder="配信を検索..." 
                        bind:value={searchQuery}
                        on:input={handleSearch}
                    />
                </div>
                
                <div class="group-filter">
                    <div class="filter-header">
                        <Filter size={16} />
                        <span>グループフィルター</span>
                    </div>
                    <div class="group-buttons">
                        <button 
                            class="group-button {selectedGroup === '' ? 'active' : ''}" 
                            on:click={() => selectGroup('')}
                        >
                            すべて
                        </button>
                        {#each groups as group}
                            <button 
                                class="group-button {selectedGroup === group.id ? 'active' : ''}" 
                                on:click={() => selectGroup(group.id)}
                                style="background-color: {group.color || '#333'};"
                            >
                                {group.name}
                            </button>
                        {/each}
                    </div>
                </div>
                
                <div class="stream-type-filter">
                    <div class="filter-header">
                        <Users size={16} />
                        <span>配信者タイプ</span>
                    </div>
                    <div class="group-buttons">
                        <button 
                            class="group-button {streamTypeFilter === 'vtuber' ? 'active' : ''}" 
                            on:click={() => selectStreamType('vtuber')}
                        >
                            VTuber
                        </button>
                        <button 
                            class="group-button {streamTypeFilter === 'streamer' ? 'active' : ''}" 
                            on:click={() => selectStreamType('streamer')}
                        >
                            Streamer
                        </button>
                    </div>
                </div>
                
                <div class="live-videos-list">
                    <div class="list-header">
                        <h3>配信中 ({filteredVideos.length})</h3>
                        <div class="update-info">
                            <span class="last-updated">最終更新: {formatTime(lastUpdated)}</span>
                        </div>
                    </div>
                    {#each filteredVideos as video (video.id)}
                        <div 
                            class="live-video-item"
                            animate:flip={{ duration: 300, easing: quintOut }}
                            on:click={() => addVideo(video)}
                        >
                            <div class="video-thumbnail">
                                <img src={video.thumbnail_url} alt={video.title} />
                                <div class="live-badge">LIVE</div>
                                <div class="viewers-count">
                                    <Users size={12} />
                                    <span>{formatNumber(video.concurrent_viewers || 0)}</span>
                                </div>
                            </div>
                            <div class="video-info">
                                <div class="channel-info">
                                    <img src={getChannelThumbnail(video.channel_id)} alt={getChannelName(video.channel_id)} class="channel-avatar" />
                                    <span class="channel-name">{getChannelName(video.channel_id)}</span>
                                </div>
                                <h4 class="video-title">{video.title}</h4>
                                <div class="video-meta">
                                    <span class="publish-date">{formatDate(video.published_at)}</span>
                                </div>
                            </div>
                            <button class="add-button">
                                <Plus size={16} />
                            </button>
                        </div>
                    {/each}
                </div>
            </aside>
        {/if}

        <!-- メインコンテンツ -->
        <main class="multiview-main" class:with-sidebar={showSidebar} class:with-chat={showChat}>
            {#if !customLayout}
                <div class="video-grid" style="grid-template-rows: repeat({layout.rows}, 1fr); grid-template-columns: repeat({layout.columns}, 1fr);">
                    {#each selectedVideos as video, index (video.id)}
                        <div 
                            class="video-container {activeVideoIndex === index ? 'active' : ''}"
                            draggable={true}
                            on:dragstart={() => dragStart(video, index)}
                            on:dragover|preventDefault={() => dragOver(index)}
                            on:drop|preventDefault={drop}
                            on:click={() => setActiveVideo(index)}
                        >
                            <div class="video-header">
                                <div class="video-title-container">
                                    <img src={getChannelThumbnail(video.channel_id)} alt={getChannelName(video.channel_id)} class="channel-avatar-small" />
                                    <span class="video-title-text">{video.title}</span>
                                </div>
                                <button class="remove-video" on:click|stopPropagation={() => removeVideo(index)}>
                                    <X size={16} />
                                </button>
                            </div>
                            <div class="video-player">
                                <iframe
                                    title={video.title}
                                    src={getVideoEmbedUrl(video, index)}
                                    frameborder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowfullscreen
                                ></iframe>
                            </div>
                        </div>
                    {/each}
                    
                    {#if selectedVideos.length === 0}
                        <div class="empty-state">
                            <div class="empty-message">
                                <Plus size={48} />
                                <p>左サイドバーから配信を選択してください</p>
                            </div>
                        </div>
                    {/if}
                </div>
            {:else}
                <!-- メイン動画を中心に表示するカスタムレイアウト -->
                <div class="custom-layout">
                    {#if selectedVideos.length > 0}
                        <div class="main-video-container">
                            <div class="video-header">
                                <div class="video-title-container">
                                    <img src={getChannelThumbnail(selectedVideos[mainVideoIndex].channel_id)} alt={getChannelName(selectedVideos[mainVideoIndex].channel_id)} class="channel-avatar-small" />
                                    <span class="video-title-text">{selectedVideos[mainVideoIndex].title}</span>
                                </div>
                                <button class="remove-video" on:click|stopPropagation={() => removeVideo(mainVideoIndex)}>
                                    <X size={16} />
                                </button>
                            </div>
                            <div class="video-player">
                                <iframe
                                    title={selectedVideos[mainVideoIndex].title}
                                    src={getVideoEmbedUrl(selectedVideos[mainVideoIndex], mainVideoIndex)}
                                    frameborder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowfullscreen
                                ></iframe>
                            </div>
                        </div>
                        <div class="sub-videos-container">
                            {#each selectedVideos as video, index (video.id)}
                                {#if index !== mainVideoIndex}
                                    <div 
                                        class="sub-video-container {activeVideoIndex === index ? 'active' : ''}"
                                        draggable={true}
                                        on:dragstart={() => dragStart(video, index)}
                                        on:dragover|preventDefault={() => dragOver(index)}
                                        on:drop|preventDefault={drop}
                                        on:click={() => setActiveVideo(index)}
                                    >
                                        <div class="video-header">
                                            <div class="video-title-container">
                                                <img src={getChannelThumbnail(video.channel_id)} alt={getChannelName(video.channel_id)} class="channel-avatar-small" />
                                                <span class="video-title-text">{video.title}</span>
                                            </div>
                                            <button class="remove-video" on:click|stopPropagation={() => removeVideo(index)}>
                                                <X size={16} />
                                            </button>
                                        </div>
                                        <div class="video-player">
                                            <iframe
                                                title={video.title}
                                                src={getVideoEmbedUrl(video, index)}
                                                frameborder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowfullscreen
                                            ></iframe>
                                        </div>
                                    </div>
                                {/if}
                            {/each}
                        </div>
                    {/if}
                </div>
            {/if}
        </main>

        <!-- チャット表示エリア -->
        {#if showChat && selectedVideos.length > 0}
            <aside class="chat-sidebar" transition:slide={{ duration: 300, axis: 'x' }}>
                <div class="chat-header">
                    <div class="chat-controls">
                        <button class="chat-mode-toggle" on:click={toggleChatMode}>
                            {#if dualChatMode}
                                <span>1つのチャットに切替</span>
                            {:else}
                                <span>2つのチャットに切替</span>
                            {/if}
                        </button>
                    </div>
                    <button class="chat-close-button" on:click={toggleChat}>
                        <X size={16} />
                    </button>
                </div>
                
                <div class="chat-container-wrapper" class:dual-mode={dualChatMode}>
                    <!-- 1つ目のチャット -->
                    <div class="chat-section" style={dualChatMode ? 'height: 50%' : 'height: 100%'}>
                        <div class="chat-section-header">
                            <div class="chat-section-title">
                                {#if dualChatMode}
                                    <span>チャット 1</span>
                                {:else}
                                    <span>チャット</span>
                                {/if}
                            </div>
                            <div class="chat-controls">
                                <select bind:value={activeChatIndices[0]} class="chat-selector">
                                    {#each selectedVideos as video, i}
                                        <option value={i}>{getChannelName(video.channel_id)}</option>
                                    {/each}
                                </select>
                            </div>
                        </div>
                        <div class="chat-container">
                            {#each selectedVideos as video, i}
                                <div class="chat-frame" class:active={activeChatIndices[0] === i}>
                                    <iframe
                                        title={`Live Chat - ${video.title}`}
                                        src={getChatEmbedUrl(video)}
                                        frameborder="0"
                                    ></iframe>
                                </div>
                            {/each}
                        </div>
                    </div>

                    <!-- 2つ目のチャット (デュアルモードの場合のみ表示) -->
                    {#if dualChatMode}
                        <div class="chat-section" style="height: 50%">
                            <div class="chat-section-header">
                                <div class="chat-section-title">
                                    <span>チャット 2</span>
                                </div>
                                <div class="chat-controls">
                                    <select bind:value={activeChatIndices[1]} class="chat-selector">
                                        {#each selectedVideos as video, i}
                                            <option value={i}>{getChannelName(video.channel_id)}</option>
                                        {/each}
                                    </select>
                                </div>
                            </div>
                            <div class="chat-container">
                                {#each selectedVideos as video, i}
                                    <div class="chat-frame" class:active={activeChatIndices[1] === i}>
                                        <iframe
                                            title={`Live Chat - ${video.title}`}
                                            src={getChatEmbedUrl(video)}
                                            frameborder="0"
                                        ></iframe>
                                    </div>
                                {/each}
                            </div>
                        </div>
                    {/if}
                </div>
            </aside>
        {/if}
    </div>
</div>

<style>
    .multiview-container {
        display: flex;
        flex-direction: column;
        height: 88vh;
        width: 100%;
        overflow: hidden;
        background-color: #0f0f0f;
        color: #fff;
    }

    .multiview-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem 1rem;
        background-color: #1a1a1a;
        border-bottom: 1px solid #333;
        height: 60px;
    }

    .header-left h1 {
        font-size: 1.5rem;
        margin: 0;
    }

    .header-right {
        display: flex;
        gap: 1rem;
        align-items: center;
    }

    .icon-button {
        background: none;
        border: none;
        color: #fff;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0.5rem;
        border-radius: 50%;
        transition: background-color 0.2s;
    }

    .icon-button:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }

    .layout-controls {
        display: flex;
        align-items: center;
        min-width: 800px;
    }

    .layout-buttons {
        display: flex;
        gap: 0.5rem;
        width: 100%;
    }

    .layout-category {
        display: flex;
    }

    .special-layouts {
        margin-left: 0.5rem;
        padding-left: 0.5rem;
        border-left: 1px solid #444;
        display: flex;
        align-items: center;
        overflow-x: auto;
        max-width: 400px;
    }
    
    .special-layouts .layout-grid {
        display: flex;
        flex-wrap: nowrap;
        gap: 0.5rem;
    }

    .layout-grid {
        display: flex;
        gap: 0.5rem;
    }

    .layout-option {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.25rem;
        background: none;
        border: none;
        color: #fff;
        cursor: pointer;
        padding: 0.25rem;
        border-radius: 4px;
        transition: background-color 0.2s;
    }

    .layout-option:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }

    .layout-option span {
        font-size: 0.7rem;
    }

    .layout-preview {
        width: 32px;
        height: 24px;
        background-color: #333;
        border-radius: 4px;
        display: grid;
        gap: 2px;
        padding: 2px;
    }

    .layout-1x1 {
        grid-template-columns: 1fr;
        grid-template-rows: 1fr;
    }

    .layout-1x1::before {
        content: "";
        background-color: #555;
        grid-column: 1;
        grid-row: 1;
        border-radius: 2px;
    }

    .layout-1x2 {
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 1fr;
    }

    .layout-1x2::before, .layout-1x2::after {
        content: "";
        background-color: #555;
        border-radius: 2px;
    }

    .layout-1x2::before {
        grid-column: 1;
        grid-row: 1;
    }

    .layout-1x2::after {
        grid-column: 2;
        grid-row: 1;
    }

    .layout-2x2 {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 1fr 1fr;
        gap: 2px;
    }

    .layout-2x2::before, .layout-2x2::after, .layout-2x2 span:nth-child(1), .layout-2x2 span:nth-child(2) {
        content: "";
        background-color: #555;
        border-radius: 2px;
    }

    .layout-2x2::before {
        grid-column: 1;
        grid-row: 1;
    }

    .layout-2x2::after {
        grid-column: 2;
        grid-row: 1;
    }

    .layout-2x2 span:nth-child(1) {
        grid-column: 1;
        grid-row: 2;
    }

    .layout-2x2 span:nth-child(2) {
        grid-column: 2;
        grid-row: 2;
    }

    .layout-2x3 {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        grid-template-rows: 1fr 1fr;
        gap: 2px;
    }

    .layout-2x3::before, .layout-2x3::after, 
    .layout-2x3 span:nth-child(1), .layout-2x3 span:nth-child(2),
    .layout-2x3 span:nth-child(3), .layout-2x3 span:nth-child(4) {
        content: "";
        background-color: #555;
        border-radius: 2px;
    }

    .layout-2x3::before {
        grid-column: 1;
        grid-row: 1;
    }

    .layout-2x3::after {
        grid-column: 2;
        grid-row: 1;
    }

    .layout-2x3 span:nth-child(1) {
        grid-column: 3;
        grid-row: 1;
    }

    .layout-2x3 span:nth-child(2) {
        grid-column: 1;
        grid-row: 2;
    }

    .layout-2x3 span:nth-child(3) {
        grid-column: 2;
        grid-row: 2;
    }

    .layout-2x3 span:nth-child(4) {
        grid-column: 3;
        grid-row: 2;
    }

    .layout-3x3 {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        grid-template-rows: 1fr 1fr 1fr;
        gap: 1px;
    }

    .layout-3x3::before {
        content: "";
        background-color: #555;
        grid-column: 1;
        grid-row: 1;
        border-radius: 2px;
    }

    .layout-3x3::after {
        content: "";
        background-color: #555;
        grid-column: 2;
        grid-row: 1;
        border-radius: 2px;
    }

    .layout-3x3 span:nth-child(1) {
        content: "";
        background-color: #555;
        grid-column: 3;
        grid-row: 1;
        border-radius: 2px;
    }

    .layout-3x3 span:nth-child(2) {
        content: "";
        background-color: #555;
        grid-column: 1;
        grid-row: 2;
        border-radius: 2px;
    }

    .layout-3x3 span:nth-child(3) {
        content: "";
        background-color: #555;
        grid-column: 2;
        grid-row: 2;
        border-radius: 2px;
    }

    .layout-3x3 span:nth-child(4) {
        content: "";
        background-color: #555;
        grid-column: 3;
        grid-row: 2;
        border-radius: 2px;
    }

    .layout-3x3 span:nth-child(5) {
        content: "";
        background-color: #555;
        grid-column: 1;
        grid-row: 3;
        border-radius: 2px;
    }

    .layout-3x3 span:nth-child(6) {
        content: "";
        background-color: #555;
        grid-column: 2;
        grid-row: 3;
        border-radius: 2px;
    }

    .layout-3x3 span:nth-child(7) {
        content: "";
        background-color: #555;
        grid-column: 3;
        grid-row: 3;
        border-radius: 2px;
    }

    .layout-1x3 {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        grid-template-rows: 1fr;
        gap: 2px;
    }

    .layout-1x3::before, .layout-1x3::after, .layout-1x3 span:nth-child(1) {
        content: "";
        background-color: #555;
        border-radius: 2px;
    }

    .layout-1x3::before {
        grid-column: 1;
        grid-row: 1;
    }

    .layout-1x3::after {
        grid-column: 2;
        grid-row: 1;
    }

    .layout-1x3 span:nth-child(1) {
        grid-column: 3;
        grid-row: 1;
    }

    .layout-3x1 {
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: 1fr 1fr 1fr;
        gap: 2px;
    }

    .layout-3x1::before, .layout-3x1::after, .layout-3x1 span:nth-child(1) {
        content: "";
        background-color: #555;
        border-radius: 2px;
    }

    .layout-3x1::before {
        grid-column: 1;
        grid-row: 1;
    }

    .layout-3x1::after {
        grid-column: 1;
        grid-row: 2;
    }

    .layout-3x1 span:nth-child(1) {
        grid-column: 1;
        grid-row: 3;
    }

    .layout-3x2 {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 1fr 1fr 1fr;
        gap: 2px;
    }

    .layout-3x2::before, .layout-3x2::after, 
    .layout-3x2 span:nth-child(1), .layout-3x2 span:nth-child(2),
    .layout-3x2 span:nth-child(3), .layout-3x2 span:nth-child(4) {
        content: "";
        background-color: #555;
        border-radius: 2px;
    }

    .layout-3x2::before {
        grid-column: 1;
        grid-row: 1;
    }

    .layout-3x2::after {
        grid-column: 2;
        grid-row: 1;
    }

    .layout-3x2 span:nth-child(1) {
        grid-column: 1;
        grid-row: 2;
    }

    .layout-3x2 span:nth-child(2) {
        grid-column: 2;
        grid-row: 2;
    }

    .layout-3x2 span:nth-child(3) {
        grid-column: 1;
        grid-row: 3;
    }

    .layout-3x2 span:nth-child(4) {
        grid-column: 2;
        grid-row: 3;
    }

    .multiview-content {
        display: flex;
        flex: 1;
        overflow: hidden;
        position: relative;
    }

    .multiview-sidebar {
        width: 320px;
        background-color: #1a1a1a;
        border-right: 1px solid #333;
        display: flex;
        flex-direction: column;
        overflow-y: auto;
    }

    .search-container {
        position: relative;
        padding: 1rem;
    }

    .search-container input {
        width: 100%;
        padding: 0.5rem 2rem 0.5rem 0.5rem;
        background-color: #333;
        border: none;
        border-radius: 4px;
        color: #fff;
    }

    .search-icon {
        position: absolute;
        right: 1.5rem;
        top: 50%;
        transform: translateY(-50%);
        color: #999;
    }

    .group-filter {
        padding: 0 1rem 1rem;
    }

    .stream-type-filter {
        padding: 0 1rem 1rem;
    }

    .filter-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
        color: #ccc;
    }

    .group-buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
    }

    .group-button {
        background-color: #333;
        border: none;
        color: #fff;
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.75rem;
        transition: opacity 0.2s;
    }

    .group-button.active {
        opacity: 1;
    }

    .group-button:not(.active) {
        opacity: 0.7;
    }

    .live-videos-list {
        padding: 0 1rem;
        flex: 1;
        overflow-y: auto;
    }

    .list-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1rem;
    }

    .update-info {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .last-updated {
        font-size: 0.8rem;
        color: #ccc;
    }

    .refresh-button {
        background-color: #333;
        border: none;
        color: #fff;
        cursor: pointer;
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
        font-size: 0.75rem;
        transition: background-color 0.2s;
    }

    .refresh-button:hover {
        background-color: #444;
    }

    .live-video-item {
        display: flex;
        margin-bottom: 1rem;
        background-color: #222;
        border-radius: 8px;
        overflow: hidden;
        cursor: pointer;
        transition: transform 0.2s, box-shadow 0.2s;
        position: relative;
        height: 80px;
    }

    .live-video-item:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    .video-thumbnail {
        width: 142px;
        height: 80px;
        position: relative;
        flex-shrink: 0;
        overflow: hidden;
        background-color: #000;
    }

    .video-thumbnail img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
    }

    .live-badge {
        position: absolute;
        bottom: 4px;
        left: 4px;
        background-color: #f00;
        color: #fff;
        font-size: 0.6rem;
        padding: 0.1rem 0.3rem;
        border-radius: 2px;
        font-weight: bold;
    }

    .viewers-count {
        position: absolute;
        bottom: 4px;
        right: 4px;
        background-color: rgba(0, 0, 0, 0.7);
        color: #fff;
        font-size: 0.6rem;
        padding: 0.1rem 0.3rem;
        border-radius: 2px;
        display: flex;
        align-items: center;
        gap: 0.2rem;
    }

    .video-info {
        padding: 0.5rem;
        flex: 1;
        min-width: 0;
    }

    .channel-info {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.25rem;
    }

    .channel-avatar {
        width: 20px;
        height: 20px;
        border-radius: 50%;
    }

    .channel-name {
        font-size: 0.7rem;
        color: #aaa;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .video-title {
        margin: 0;
        font-size: 0.8rem;
        line-height: 1.2;
        max-height: 2.4em;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
    }

    .video-meta {
        font-size: 0.7rem;
        color: #999;
        margin-top: 0.25rem;
    }

    .add-button {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        background-color: rgba(0, 0, 0, 0.5);
        border: none;
        color: #fff;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        opacity: 0;
        transition: opacity 0.2s;
    }

    .live-video-item:hover .add-button {
        opacity: 1;
    }

    .multiview-main {
        flex: 1;
        overflow: hidden;
        background-color: #0f0f0f;
    }

    .multiview-main.with-sidebar.with-chat {
        flex: 1;
    }

    .video-grid {
        display: grid;
        gap: 4px;
        height: 100%;
        padding: 4px;
    }

    .video-container {
        background-color: #000;
        border-radius: 4px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        border: 1px solid #333;
        transition: border-color 0.2s;
    }

    .video-container.active {
        border-color: #1a73e8;
    }

    .video-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.25rem 0.5rem;
        background-color: #1a1a1a;
        height: 32px;
    }

    .video-title-container {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        overflow: hidden;
    }

    .channel-avatar-small {
        width: 16px;
        height: 16px;
        border-radius: 50%;
    }

    .video-title-text {
        font-size: 0.7rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .remove-video {
        background: none;
        border: none;
        color: #999;
        cursor: pointer;
        padding: 0.25rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: background-color 0.2s, color 0.2s;
    }

    .remove-video:hover {
        background-color: rgba(255, 255, 255, 0.1);
        color: #fff;
    }

    .video-player {
        flex: 1;
        position: relative;
    }

    .video-player iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }

    .empty-state {
        grid-column: 1 / -1;
        grid-row: 1 / -1;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .empty-message {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
        color: #666;
    }

    .chat-sidebar {
        width: 350px;
        background-color: #0f0f0f;
        border-left: 1px solid #333;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }
    
    .chat-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 12px;
        border-bottom: 2px solid #444;
        background-color: #1a1a1a;
    }
    
    .chat-close-button {
        background: none;
        border: none;
        color: var(--text-color);
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
    }
    
    .chat-close-button:hover {
        background-color: var(--hover-color);
    }
    
    .chat-container-wrapper {
        display: flex;
        flex-direction: column;
        flex: 1;
        overflow: hidden;
    }
    
    .chat-section {
        display: flex;
        flex-direction: column;
        flex: 1;
        overflow: hidden;
        position: relative;
    }
    
    .chat-section:not(:last-child) {
        border-bottom: 2px solid #444;
    }
    
    .chat-section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 12px;
        border-bottom: 1px solid var(--border-color, #333);
        background-color: #1a1a1a;
    }
    
    .chat-controls {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    
    .chat-mode-toggle {
        background-color: #333;
        color: white;
        border: none;
        border-radius: 4px;
        padding: 6px 10px;
        font-size: 14px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 6px;
    }
    
    .chat-mode-toggle:hover {
        background-color: #444;
    }
    
    .chat-selector {
        background-color: #222;
        color: #fff;
        border: 1px solid #444;
        border-radius: 4px;
        padding: 4px 8px;
        font-size: 12px;
    }
    
    .chat-selector option {
        background-color: #222;
        color: #fff;
    }
    
    .chat-container {
        position: relative;
        flex: 1;
        overflow: hidden;
    }
    
    .chat-frame {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: none;
    }
    
    .chat-frame.active {
        display: block;
    }
    
    .chat-frame iframe {
        width: 100%;
        height: 100%;
        border: none;
    }
    
    /* デュアルモード時のスタイル調整 */
    .dual-mode .chat-section {
        height: 50%;
        position: relative;
    }

    .dual-mode .chat-section:first-child::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 2px;
        background-color: #444;
        z-index: 2;
    }

    /* レスポンシブ対応 */
    @media (max-width: 1200px) {
        .layout-buttons {
            max-width: 400px;
        }
    }

    @media (max-width: 992px) {
        .layout-buttons {
            max-width: 300px;
        }
    }

    @media (max-width: 768px) {
        .multiview-sidebar {
            position: absolute;
            top: 60px;
            left: 0;
            bottom: 0;
            z-index: 10;
            width: 100%;
        }

        .chat-sidebar {
            position: absolute;
            top: 60px;
            right: 0;
            bottom: 0;
            z-index: 10;
            width: 100%;
        }

        .layout-controls {
            display: none;
        }
    }

    .main-layout-preview {
        background-color: #333;
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 32px;
        height: 24px;
        border-radius: 4px;
    }

    .main-number {
        font-size: 0.8rem;
        font-weight: bold;
        color: #fff;
    }

    .layout-option.active {
        border: 2px solid #3b82f6;
        background-color: rgba(59, 130, 246, 0.1);
    }
    
    .layout-option.active .layout-preview {
        border-color: #3b82f6;
    }

    .sidebar-toggle-button {
        background-color: #333;
        border: none;
        color: #fff;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 0.75rem;
        border-radius: 4px;
        transition: background-color 0.2s;
        font-size: 0.8rem;
    }

    .sidebar-toggle-button:hover {
        background-color: #444;
    }

    .sidebar-toggle-button span {
        display: inline-block;
    }

    .chat-section-title {
        font-weight: bold;
        font-size: 14px;
        color: #fff;
    }

    /* カスタムレイアウト用のスタイル */
    .custom-layout {
        display: flex;
        flex-direction: column;
        height: 100%;
        padding: 4px;
        gap: 4px;
    }
    
    .main-video-container {
        flex: 3;
        background-color: #000;
        border-radius: 4px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        border: 1px solid #333;
    }
    
    .sub-videos-container {
        flex: 1;
        display: flex;
        gap: 4px;
        overflow-x: auto;
    }
    
    .sub-video-container {
        flex: 1;
        min-width: 200px;
        background-color: #000;
        border-radius: 4px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        border: 1px solid #333;
        transition: border-color 0.2s;
    }
    
    .sub-video-container.active {
        border-color: #1a73e8;
    }
</style> 